import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import TextButton from 'LuxKit/Button/TextButton'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ModalContext from 'contexts/ModalContext'

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(12)};
`

const TotalRight = styled.div`
  text-align: right;
  flex-shrink: 0;
`

interface Props {
  totalPrice: number
  totalItemCount: number
  hasCheckoutReadyItems: boolean
  showReadyItemsOnlyNote: boolean
  isLoggedIn: boolean
  onCheckout: () => Promise<void>
}

function CartPanelFooter({
  totalPrice,
  totalItemCount,
  hasCheckoutReadyItems,
  showReadyItemsOnlyNote,
  isLoggedIn,
  onCheckout,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const handleCheckoutClick = useCallback(() => {
    if (!isLoggedIn) {
      showModal(<AccountAccessModal />)
      return
    }
    onCheckout()
  }, [isLoggedIn, showModal, onCheckout])

  const totalHeading = intl.formatMessage(
    {
      id: 'wrcligwUpGIpkDJ9iUs3',
      defaultMessage: 'Total ({count, plural, one {# item} other {# items}})',
      description: 'Cart panel footer heading showing total with item count',
    },
    { count: totalItemCount },
  )

  const checkoutLabel = !isLoggedIn
    ? intl.formatMessage({
        id: 'PsWbPJA5WZimhO6Dv0Zl',
        defaultMessage: 'Log in to checkout',
        description: 'Cart panel checkout button text when user is not logged in',
      })
    : intl.formatMessage({
        id: '5tiwR3KtXkphPkrPc0tW',
        defaultMessage: 'Checkout now',
        description: 'Primary checkout button text shown in the trip planner cart panel footer',
      })

  return (
    <Sheet padding={16} background="normal-background">
      <Group direction="vertical" gap={12}>
        <TotalRow>
          <div>
            <Heading variant="heading5">{totalHeading}</Heading>
            <PriceRowTaxesAndFees />
          </div>
          <TotalRight>
            <PriceRowPrice
              price={totalPrice}
              priceFormat="preciseDollar"
              saleUnit=""
              size="M"
            />
          </TotalRight>
        </TotalRow>

        {showReadyItemsOnlyNote && (
          <BodyText variant="small" colour="neutral-three">
            {intl.formatMessage({
              id: 'hv52vMT3ulBalYfuWSIX',
              defaultMessage: 'Checkout includes ready items only',
              description: 'Cart panel footer note explaining checkout skips items that need user attention',
            })}
          </BodyText>
        )}

        <TextButton
          kind="primary"
          size="large"
          disabled={!hasCheckoutReadyItems && isLoggedIn}
          onClick={handleCheckoutClick}
        >
          {checkoutLabel}
        </TextButton>
      </Group>
    </Sheet>
  )
}

export default CartPanelFooter
